/**
 * Energie auf dem Gerät: Bedarf, Sport des Tages, Bilanz, Zielvorschlag.
 *
 * Gerechnet wird in `@kraftwerk/shared` (energy.ts); hier wird nur
 * zusammengetragen, was die Rechnung braucht — Profil, Protokoll, Einheiten,
 * Trainingstermine.
 */

import {
  activityKcal,
  basalMetabolicRate,
  baseNeed,
  dayBalance,
  isoDateInZone,
  missingFacts,
  suggestGoal,
  trainingOccurrences,
} from '@kraftwerk/shared';
import type { DayBalance, DayExercise, GoalSuggestion, MissingFact } from '@kraftwerk/shared';
import { db } from './local.js';
import type { KraftwerkDatabase } from './local.js';
import { listLog } from './nutrition.js';
import { readProfile } from './profile.js';
import { listSlots } from './schedule.js';
import { calendarExercises, listAgenda } from './calendar.js';

/** Für den Fall, dass weder Termin noch Ende eine Dauer nennen. */
const FALLBACK_SESSION_MIN = 60;

export type EnergyOfDay =
  | { ready: false; missing: (MissingFact | 'activityLevel')[] }
  | {
      ready: true;
      bmr: number;
      balance: DayBalance;
      exercises: DayExercise[];
      /** Bedarf eines typischen Tages mit Sport — Grundlage des Zielvorschlags. */
      typicalNeed: number;
      suggestion: GoalSuggestion | null;
    };

/**
 * Bedarf und Bilanz eines Tages.
 *
 * Sport: Erledigte Einheiten zählen mit ihrer Dauer; geplante Termine ohne
 * Einheit zählen als **erwartet**, abgesagte gar nicht. Eine laufende Einheit
 * zählt bis jetzt.
 */
export async function energyOfDay(
  date: string,
  timeZone: string,
  database: KraftwerkDatabase = db,
  now: Date = new Date(),
  /** Eigenes Konto — nur eigene Haushaltstermine zählen in der Bilanz. */
  me: string | null = null,
): Promise<EnergyOfDay> {
  // Alles in **einem** Schritt lesen: Live-Abfragen verlieren sonst die
  // Beobachtung hinter verschachtelten `await` (CLAUDE.md).
  const [profile, entries, sessions, slots, days, agenda] = await Promise.all([
    readProfile(database),
    listLog(date, database),
    database.workout_sessions.toArray(),
    listSlots(database),
    database.training_days.toArray(),
    listAgenda(date, date, { timeZone, me, trainingTitle: '' }, database),
  ]);
  const facts = {
    birthYear: profile.body.birthYear,
    sex: profile.body.sexFormula,
    heightCm: profile.body.heightCm,
    weightKg: profile.weightKg,
  };
  const missing: (MissingFact | 'activityLevel')[] = missingFacts(facts);
  if (!profile.body.activityLevel) missing.push('activityLevel');
  const bmr = basalMetabolicRate(facts, Number(date.slice(0, 4)));
  if (missing.length > 0 || bmr === null || !profile.body.activityLevel) {
    return { ready: false, missing };
  }
  const weightKg = profile.weightKg!;

  const exercises: DayExercise[] = [];
  const sessionsToday = sessions.filter(
    (session) =>
      !session.deletedAt && isoDateInZone(new Date(session.startedAt), timeZone) === date,
  );
  for (const session of sessionsToday) {
    const end = session.endedAt ? new Date(session.endedAt) : now;
    const minutes = Math.max(
      0,
      Math.round((end.getTime() - Date.parse(session.startedAt)) / 60_000),
    );
    exercises.push({
      activity: 'strength',
      // Eine vergessene, nie beendete Einheit soll nicht 14 Stunden Training zählen.
      minutes: session.endedAt ? minutes : Math.min(minutes, 3 * 60),
      state: 'done',
    });
  }
  if (sessionsToday.length === 0) {
    const live = days.filter((row) => !row.deletedAt);
    for (const occurrence of trainingOccurrences(slots, live, date, date, timeZone)) {
      if (occurrence.status !== 'planned') continue;
      exercises.push({
        activity: 'strength',
        minutes: occurrence.durationMin || FALLBACK_SESSION_MIN,
        state: 'planned',
      });
    }
  }

  // Aktivitäten aus dem Kalender (Wandern, Rad …) — Training steht oben schon.
  exercises.push(...calendarExercises(agenda, date, timeZone, now));

  const base = baseNeed(bmr, profile.body.activityLevel);
  const eaten = entries.reduce((sum, entry) => sum + entry.kcal, 0);

  // Typischer Tag: Grundbedarf plus die Trainingswoche aus der Regel, auf sieben Tage verteilt.
  const weeklyTraining = slots
    .filter((slot) => slot.active)
    .reduce((sum, slot) => sum + activityKcal('strength', slot.durationMin, weightKg), 0);
  const typicalNeed = Math.round(base + weeklyTraining / 7);

  return {
    ready: true,
    bmr,
    balance: dayBalance(base, exercises, weightKg, eaten),
    exercises,
    typicalNeed,
    suggestion: profile.body.goal
      ? suggestGoal(typicalNeed, weightKg, profile.body.goal, profile.body.goalRatePct ?? undefined)
      : null,
  };
}
